import * as d3 from 'd3';

import { Plot } from './Plot';
import { VerticalBrush } from './VerticalBrush';
import { SeriesFormat } from './SeriesFormat';
import { RoundingHelper } from '../../../helpers/helpers';
import { getRandomInt32 } from '../../../../common/helpers/getRandom';

export class RangeChartPane<T, U> {
    private _brush: VerticalBrush;
    private _plot: Plot;
    private _brushCallback: (extent: [number, number]) => void;
    private _seriesFormat: SeriesFormat<T, U>;
    private _clippath;
    private _roundingHelper: RoundingHelper;

    constructor(container: d3.Selection<any>, width: number, height: number) {
        this.initChart(container, width, height);
        this._roundingHelper = new RoundingHelper();
    }

    private initChart(container: d3.Selection<any>, width: number, height: number): void {
        const id = `clippath${getRandomInt32()}`;
        this._clippath = container.append('defs')
            .append('clipPath')
            .attr('id', id)
            .append('rect')
            .classed('clippath-rectangle', true)
            .attr({
                'x': 0,
                'y': 0,
                'width': width,
                'height': height
            });
        container.style({
            'clip-path': `url("#${id}")`
        });

        this.rebuildBrush(container, width, height);
    }

    rebuildBrush(container, width, height) {
        this._brush = new VerticalBrush(container, 20, height);
        const brushWidth = this._brush.width();
        const plotGroup = container.append('g')
            .attr('transform', `translate(${brushWidth},0)`);
        this._plot = new Plot(plotGroup, width - brushWidth, height);
        this._brush.dispatch()
            .on('brush', extent => {
                this._plot.updateArea(extent);
            });
        this._brush.dispatch()
            .on('brushed', (extent) => {
                if (this._brushCallback) {
                    this._brushCallback(extent);
                }
            });

        return this;
    }

    update(data: T[]) {
        const values = data
            .filter(d => this._seriesFormat.selector(d) === this._seriesFormat.identifier)
            .map((d: any) => {
                return {
                    timeStamp: d.timeStamp,
                    value: this._seriesFormat.value(d)
                };
            })
            .filter(d => !!d.value && d.value !== 0);
        this._data = values;
        this.updateRange();
        this._plot.update(values);
    }


    resize(width: number, height: number) {
        this._clippath.attr({
            'width': width,
            'height': height
        });
        this._plot.resize(width - this._brush.width(), height);
        this._brush.resize(20, height);
        this.updateRange();
    }

    brushCallback(value: (extent: [number, number]) => void): void {
        if (arguments.length > 0) {
            this._brushCallback = value;
        }
    }

    seriesFormat(seriesFormat: SeriesFormat<T, U>) {
        this._seriesFormat = seriesFormat;
        this._plot.seriesName(seriesFormat.name);
    }

    range(range: [number, number]): void {
        this._range = range;
        this.updateRange();
    }

    isLockedBrush(isLockedBrush: boolean) {
        this._brush.isLocked(isLockedBrush);
    }

    isBrushDisabled(value: boolean): void {
        this._brush.isDisabled(value);
    }

    updateCrosshair(date: Date) {
        this._plot.updateCrosshair(date);
        return this;
    }

    onCrosshair(callback) {
        this._plot.onCrosshair(callback);
        return this;
    }

    updateLines(lines: { value: number, color: string }[]) {
        this._plot.updateLines(lines);
        return this;
    }

    private updateRange() {
        const domain = this.computeDomain(this._data ? this._data : [], this._range ? this._range : [null, null]);
        this._plot.setDomain(domain);
        this._brush.setDomain(domain);
        if (this._range) {
            const extent: [number, number] = [this._range[0], this._range[1]];
            if (!extent[0] && extent[0] !== 0) {
                extent[0] = domain[0];
            }
            if (!extent[1] && extent[1] !== 0) {
                extent[1] = domain[1];
            }
            this._plot.updateArea(extent);
            this._brush.setExtent(extent);
        }
    }

    private computeDomain(data: any[], range: [number, number]): [number, number] {
        let result: [number, number] = [0, 1];
        let domain: [number, number] = [Number.MAX_VALUE, -Number.MAX_VALUE];
        let extent: [number, number] = [Number.MAX_VALUE, -Number.MAX_VALUE];
        const hasDomain = data && data.length > 0;
        const hasRange = range && range.length > 0;
        if (hasDomain) {
            domain = d3.extent(data, (d: any) => d.value);
        }
        if (hasRange) {
            extent = [d3.min(range), d3.max(range)];
        }
        let min = (extent[0] || extent[0] === 0) ? Math.min(domain[0], extent[0]) : (hasDomain ? domain[0] : 0);
        let max = (extent[1] || extent[1] === 0) ? Math.max(domain[1], extent[1]) : (hasDomain ? domain[1] : 0);
        if (min > max) {
            [min, max] = [max, min];
        }
        const rounded = this._roundingHelper.round(min * 0.9, max * 1.1);
        result = [rounded.min, rounded.max];
        return result;
    }

    private _range: [number, number];
    private _data: { timeStamp; value: number }[];

    xDomain(xDomain: [string, string]): void {
        this._plot.xDomain(xDomain);
    }
}